<script setup lang="ts">
import { IReview, IReviewStatistic } from '@/repositories/review.repository';
import { computed, ref } from 'vue';
import Rating from '../Rating.vue';
import { formatDate } from '@/helpers/date.helper';
import CreateReviewModal from '../Modals/CreateReviewModal.vue';
import { useUrlSearchParams } from '@/helpers/url.helper';

const params = useUrlSearchParams();

const props = defineProps<{
    reviewStatistic?: IReviewStatistic;
    reviews?: IReview[];
}>();

const emits = defineEmits<{
    (e: "close"): void;
}>();

const showCreateReviewModal = ref(false);

const handleClose = () => {
    emits("close");
};

const percentage = computed(() => Math.floor(props.reviewStatistic?.AanbevelingsPercentage ?? 0))
</script>

<template>
    <div class="review-overview">
        <div class="review-overview__close">
            <button @click="handleClose" class="btn" aria-label="Sluiten">
                <span class="icon icon-close"></span>
            </button>
        </div>

        <div class="review-overview__summary">
            <template v-if="reviewStatistic && reviewStatistic?.Aantal > 0">
                <div class="review-overview__summary-percentage">
                    <span class="h3">{{ percentage }}%</span>
                    <p>van de mensen die een review achterlieten raadt deze club aan</p>
                </div>

                <div class="review-overview__summary-rating">
                    <div class="review-overview__summary-rating-item" v-for="antwoord in reviewStatistic?.Antwoorden"
                        :key="antwoord.Id">
                        <span>{{ antwoord.Titel }}</span>
                        <Rating class="review-overview__summary-rating-item-rating" :rating="antwoord.Antwoord" />
                    </div>
                    <div class="review-overview__summary-rating-item">
                        <span>Aanbeveling</span>
                        <div class="icon-thumbs-up thumb"
                            :class="[reviewStatistic && reviewStatistic.AanbevelingsPercentage > 50 ? 'rating-true' : 'rating-false']"
                            :aria-hidden="true"></div>
                    </div>
                </div>
            </template>
            <template v-else>
                <span class="text-center">Er zijn nog geen reviews gegeven, schrijf als eerst een review</span>
            </template>

            <button class="btn btn-secondary" @click="showCreateReviewModal = true">Schrijf een review</button>
        </div>

        <div class="review-overview__review" v-for="review in reviews" :key="review.Id" :id="`reviews-${review.Id}`">
            <h2 class="h4 review-overview__review-title">{{ review?.Title }}</h2>
            <Rating class="review-overview__review-rating" :rating="review.Value" />

            <div class="review-overview__review-rating-summary">
                <div class="review-overview__review-rating-summary-item" v-for="antwoord in review?.Antwoorden"
                    :key="antwoord.Id">
                    <span>{{ antwoord.Titel }}</span>
                    <Rating class="review-overview__review-rating-summary-item-rating" :rating="antwoord.Antwoord" />
                </div>
                <div class="review-overview__review-rating-summary-item">
                    <span>Aanbeveling</span>
                    <div class="icon-thumbs-up thumb"
                        :class="[reviewStatistic && reviewStatistic.AanbevelingsPercentage > 50 ? 'rating-true' : 'rating-false']"
                        :aria-hidden="true"></div>
                </div>
            </div>
            <p class="review-overview__review-description">{{ review?.Description }}</p>
            <p class="review-overview__review-author">{{ review?.Alias }} op {{ formatDate(review?.DateCreated) }}</p>
        </div>
    </div>

    <CreateReviewModal :show="showCreateReviewModal" :sportaanbieder-id="params.getParam('id') ?? ''"
        @close="showCreateReviewModal = false" />
</template>